import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { SxProps, Theme, useTheme } from "@mui/material/styles";

import { ISlideNavButtonsProperties } from "@components/Slides/SlideNavButtons";

import routes from "@constants/routes";

import { convertArrayToQueryString } from "@hooks/useFilters";

import { OrganizationType } from "@interfaces/organization.type";

import { useCreateCampaignQuery } from "@queries/campaign.query";
import { useTechnologyQuery } from "@queries/portfolio.query";

import { useTableStore } from "@store/filters/filters.store";
import { usePopupActions } from "@store/popupStore";

interface IUseTechnologyNavButtonParameters {
  organization?: OrganizationType;
}

interface ITechnologyView extends ISlideNavButtonsProperties {
  backButtonText: string;
  backButtonTooltip: string;
  handleBack: () => void;
  handleNext: () => void;
  isBackButtonDisabled: boolean;
  isNextButtonDisabled: boolean;
  isNextButtonPending: boolean;
  isTab: boolean;
  name: string;
  nextButtonStyle: SxProps<Theme>;
  nextButtonText: string;
  nextButtonTooltip: string;
  showNextButton: boolean;
  splatString: string;
}

const useTechnologyViews = ({ organization }: IUseTechnologyNavButtonParameters) => {
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();

  const { mutateAsync: createCampaign } = useCreateCampaignQuery();

  const { selection: selectedTechnologyCompanyIds } = useTableStore("technologyCompany");

  const { selection: selectedTechnologyContactIds } = useTableStore("technologyContact");

  const { technologyId } = useParams();

  const { data: technology } = useTechnologyQuery(technologyId);

  const popupActions = usePopupActions();

  const handleBackToPortfolio = useCallback(() => {
    if (organization) {
      navigate(`${routes.ORGANIZATION}/${organization.id}`);
    } else {
      navigate(routes.PORTFOLIO);
    }
  }, [navigate, organization]);

  const technologyViews = useMemo<ITechnologyView[]>(
    () => [
      // TODO: Connect this to actual data:
      // {
      //   // Technology Overview =========================================================================================
      //   backButtonText: "Portfolio",
      //   backButtonTooltip: "",
      //   handleBack: handleBackToPortfolio,
      //   handleNext: () => {
      //     /* Do Nothing */
      //   },
      //   isBackButtonDisabled: false,
      //   isNextButtonDisabled: true,
      //   isNextButtonPending: false,
      //   isTab: true,
      //   name: "Overview",
      //   nextButtonStyle:
      //     selectedTechnologyCompanyIds.length > 20
      //       ? {
      //           "&:hover": {
      //             backgroundColor: theme.palette.error.dark,
      //             color: theme.palette.error.contrastText,
      //           },
      //           backgroundColor: theme.palette.error.main,
      //           cursor: "not-allowed",
      //         }
      //       : {},
      //   nextButtonText: "",
      //   nextButtonTooltip: "",
      //   showNextButton: false,
      //   splatString: "overview",
      // },
      {
        // Technology Overview =========================================================================================
        backButtonText: "Portfolio",
        backButtonTooltip: "",
        handleBack: handleBackToPortfolio,
        handleNext: () => {
          /* Do Nothing */
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: true,
        isNextButtonPending: false,
        isTab: true,
        name: "Details",
        nextButtonStyle:
          selectedTechnologyCompanyIds.length > 20
            ? {
                "&:hover": {
                  backgroundColor: theme.palette.error.dark,
                  color: theme.palette.error.contrastText,
                },
                backgroundColor: theme.palette.error.main,
                cursor: "not-allowed",
              }
            : {},
        nextButtonText: "",
        nextButtonTooltip: "",
        showNextButton: false,
        splatString: "details",
      },
      {
        // Technology Companies ========================================================================================
        backButtonText: "Portfolio",
        backButtonTooltip: "",
        handleBack: handleBackToPortfolio,
        handleNext: () => {
          if (selectedTechnologyCompanyIds.length > 20) return;
          const urlSafeIds = convertArrayToQueryString("companyIds", selectedTechnologyCompanyIds);
          navigate(`${routes.TECHNOLOGY}/${technologyId}/companies/contact-results?${urlSafeIds}`);
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: selectedTechnologyCompanyIds.length === 0,
        isNextButtonPending: false,
        isTab: true,
        name: "Companies",
        nextButtonStyle:
          selectedTechnologyCompanyIds.length > 20
            ? {
                "&:hover": {
                  backgroundColor: theme.palette.error.dark,
                  color: theme.palette.error.contrastText,
                },
                backgroundColor: theme.palette.error.main,
                cursor: "not-allowed",
              }
            : {},
        nextButtonText: "Get Contacts",
        nextButtonTooltip:
          selectedTechnologyCompanyIds.length > 20
            ? "You can request contacts for up to 20 companies at a time."
            : selectedTechnologyCompanyIds.length === 0
              ? "Select companies to get contacts"
              : "Get contacts for selected companies",
        showNextButton: true,
        splatString: "companies",
      },
      {
        // Technology Contacts =========================================================================================
        backButtonText: "Portfolio",
        backButtonTooltip: "",
        handleBack: handleBackToPortfolio,
        handleNext: () => {
          if (technology?.contacts) {
            const selectedContactEmails = technology.contacts
              .filter((contact) => selectedTechnologyContactIds.includes(contact.id) && contact.email)
              .map((contact) => contact.email);
            if (selectedContactEmails.length === 0) {
              popupActions.openNotification({
                content:
                  "We do not have the emails for the contacts you selected. Please select contacts with an email address.",
              });
            } else {
              if (!technologyId) return;
              void createCampaign({ contactIds: selectedTechnologyContactIds, technologyId: technologyId });
            }
          }
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: selectedTechnologyContactIds.length === 0,
        isNextButtonPending: false,
        isTab: true,
        name: "Contacts",
        nextButtonStyle: {},
        nextButtonText: "Create Campaign",
        nextButtonTooltip: "Send an email Campaign to selected contacts.",
        showNextButton: true,
        splatString: "contacts",
      },
      {
        // Technology Campaigns ========================================================================================
        backButtonText: "Portfolio",
        backButtonTooltip: "",
        handleBack: handleBackToPortfolio,
        handleNext: () => {
          /* Do Nothing */
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: true,
        isNextButtonPending: false,
        isTab: true,
        name: "Campaigns",
        nextButtonStyle: {},
        nextButtonText: "",
        nextButtonTooltip: "",
        showNextButton: false,
        splatString: "campaigns",
      },
      {
        // Marketing Summary ===========================================================================================
        backButtonText: "Portfolio",
        backButtonTooltip: "",
        handleBack: handleBackToPortfolio,
        handleNext: () => {
          /* Do Nothing */
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: true,
        isNextButtonPending: false,
        isTab: true,
        name: "Marketing Summary",
        nextButtonStyle: {},
        nextButtonText: "",
        nextButtonTooltip: "",
        showNextButton: false,
        splatString: "marketing-summary",
      },
      {
        // Contacts for Selected Technology Companies ==================================================================
        backButtonText: "Back to Technology",
        backButtonTooltip: "Back to Technology",
        handleBack: () => {
          if (technology) {
            navigate(`${routes.TECHNOLOGY}/${technology.id}/companies`);
          }
        },
        handleNext: () => {
          /* Do Nothing */
        },
        isBackButtonDisabled: false,
        isNextButtonDisabled: true,
        isNextButtonPending: false,
        isTab: false,
        name: "Technology Company Contacts",
        nextButtonStyle: {},
        nextButtonText: "",
        nextButtonTooltip: "",
        showNextButton: false,
        splatString: "companies/contact-results",
      },
    ],
    [
      selectedTechnologyCompanyIds.length,
      selectedTechnologyContactIds.length,
      theme.palette.error.contrastText,
      theme.palette.error.dark,
      theme.palette.error.main,
    ],
  );

  const viewIndex = useMemo(
    () => technologyViews.findIndex((slide) => location.pathname.endsWith(slide.splatString)),
    [technologyViews, location.pathname],
  );

  return {
    technologyViews,
    viewIndex,
  };
};

export default useTechnologyViews;
